<template>
  <van-row justify="center" class="page-wrapper">
    <loader :show="showLoader" />
    <payment-flow-animation
      v-if="showPaymentAnimation"
      :result="paymentAnimationResult"
      flow="payment"
      :success-action-label="animationActionLabels.success"
      :error-action-label="animationActionLabels.error"
      @action="handleAnimationAction"
    />
    <template v-if="account">
      <van-col
        v-if="paymentUrl"
        span="24"
        class="itserve-center flex-display-fix payment-iframe-wrap"
      >
        <div class="iframe-container">
          <iframe
            :src="paymentUrl"
            allowpaymentrequest
            frameborder="0"
            class="payment-iframe"
            ref="payPage"
          ></iframe>
        </div>
      </van-col>

      <template v-else>
        <van-col span="24" class="itserve-center flex-display-fix">
          <div class="payment-screen">
            <div class="summary-card">
              <div class="summary-info-row">
                <span class="summary-info-label">{{ ui.accountLabel }}</span>
                <span class="summary-info-value">№ {{ account.ls }}</span>
              </div>
              <div class="summary-info-row">
                <span class="summary-info-label">{{ debtLabel }}</span>
                <span class="summary-debt-value" :class="debtValueClass">
                  {{ formattedDebt }} {{ ui.currency }}
                </span>
              </div>
            </div>

            <div v-if="cards.length" class="saved-cards-section">
              <div class="section-title">{{ ui.chooseCardTitle }}</div>
              <div class="saved-cards-list">
                <button
                  v-for="card in savedCards"
                  :key="card.id"
                  type="button"
                  class="saved-card-item"
                  @click="selectSavedCard(card.id)"
                >
                  <div class="saved-card-main">
                    <img
                      :src="card.brandImage"
                      class="card-type-image"
                    />
                    <div class="card-number">{{ card.masked }}</div>
                  </div>
                  <div class="card-action">
                    {{ ui.payAction }}
                    <span class="card-action-arrow">→</span>
                  </div>
                </button>
              </div>
              <div v-if="!canPay" class="helper-message">
                {{ ui.helperMessage }}
              </div>
            </div>

            <div v-if="cards.length" class="divider-text">
              {{ ui.orEnterNewCard }}
            </div>

            <div class="payment-form-card">
              <div class="summary-info-row">
                <span class="summary-info-label">{{ ui.accountLabel }}</span>
                <span class="summary-info-value">№ {{ account.ls }}</span>
              </div>
              <div class="summary-info-row payment-form-summary-row">
                <span class="summary-info-label">{{ debtLabel }}</span>
                <span class="summary-debt-value" :class="debtValueClass">
                  {{ formattedDebt }} {{ ui.currency }}
                </span>
              </div>
              <div class="payment-form-divider"></div>

            <div class="section-title section-title-left">
              {{ ui.enterCardForPayment }}
            </div>

            <div class="form-group">
              <label for="cardNumber">{{ ui.cardNumberLabel }}</label>
              <input
                id="cardNumber"
                v-model="cardData.number"
                type="text"
                inputmode="numeric"
                maxlength="19"
                placeholder="XXXX XXXX XXXX XXXX"
                class="payment-input"
                @input="formatCardNumber"
              />
            </div>

            <div class="card-meta-row">
              <div class="form-group form-group-half">
                <label for="cardExpiry">{{ ui.expiryLabel }}</label>
                <input
                  id="cardExpiry"
                  v-model="cardData.expiry"
                  type="text"
                  inputmode="numeric"
                  maxlength="5"
                  placeholder="XX/XX"
                  class="payment-input"
                  @input="formatExpiry"
                />
              </div>

              <div class="form-group form-group-half">
                <label for="cardCvv">CVV</label>
                <input
                  id="cardCvv"
                  v-model="cardData.cvv"
                  type="password"
                  inputmode="numeric"
                  maxlength="3"
                  placeholder="XXX"
                  class="payment-input"
                  @input="formatCvv"
                />
              </div>
            </div>

            <div v-if="validationError" class="error-message">
              {{ validationError }}
            </div>

            <div class="amount-entry">
              <label for="amountInput" class="amount-entry-label">
                {{ ui.amountLabel }}
              </label>
              <input
                id="amountInput"
                v-model="amountInput"
                type="text"
                inputmode="decimal"
                placeholder="0.00"
                class="amount-value-input"
                @input="formatAmount"
              />
            </div>
            </div>

            <div class="pay-button-wrap">
              <button
                type="button"
                class="submit-button"
                :disabled="!canPay"
                @click="submitPayment"
              >
                {{ payButtonLabel }}
              </button>
            </div>

            <div class="security-badge">
              <span class="security-lock">🔒</span>
              <span>{{ ui.securePayment }}</span>
              <img src="@/assets/images/visa.png" class="security-card" />
              <img src="@/assets/images/mastercard.png" class="security-card" />
            </div>

            <div class="debt-note">
              <div class="debt-note-text">
                {{ $t("pay.debtNotification") }}
              </div>
            </div>
          </div>
        </van-col>

        <add-phone v-if="showPopup"></add-phone>
      </template>
    </template>
  </van-row>
</template>

<script>
import Loader from "@/components/content/Loader";
import PaymentFlowAnimation from "@/components/payment/PaymentFlowAnimation";
import AddPhone from "@/components/popup/addPhone";

const STRIPE_SCRIPT_ID = "stripe-js-v3";
const PAYMENT_FLOW_SESSION_KEY = "paymentFlowInProgress";
const MIN_PAYMENT_AMOUNT = 3;
const STRIPE_PUBLISHABLE_KEY =
  "pk_live_51TOe5yGpDasnTuCalzP3Keo8PUlwE01PtY6CSyEyQ8QURzCaGXE13kqkovgoPmitxOxJuUad1lcl7GF4dD5Rrkfq00YEOrWhV6";
const STRIPE_3DS_CANCELED_MESSAGE =
  "Підтвердження в банку скасовано або не завершено. Спробуйте ще раз і підтвердіть дію в застосунку банку.";

export default {
  name: "Pay",
  components: {
    AddPhone,
    Loader,
    PaymentFlowAnimation,
  },
  data() {
    return {
      showPopup: false,
      validationError: null,
      watchInterval: null,
      showPaymentAnimation: false,
      paymentAnimationResult: null,
      selectedSavedCardId: null,
      amountInput: "",
      fallbackPhone: "",
      stripe: null,
      stripePublishableKey: null,
      cardData: {
        number: "",
        expiry: "",
        cvv: "",
      },
    };
  },
  computed: {
    showLoader() {
      return this.$store.state.Api.loading;
    },
    cards() {
      return this.$store.state.Payment.cards;
    },
    savedCards() {
      return this.cards.map((card) => {
        const brand = String(card.brand || "").toLowerCase();
        const isVisa =
          brand.includes("visa") ||
          String(card.pan || card.masked || "").trim().startsWith("4");

        return {
          id: card.card_id || card.card_token,
          masked: card.masked || card.pan || "",
          brandImage: isVisa
            ? require("@/assets/images/visa.png")
            : require("@/assets/images/mastercard.png"),
        };
      });
    },
    paymentUrl() {
      return this.$store.state.Payment.paymentUrl;
    },
    paymentResponse() {
      return this.$store.state.Payment.paymentResponse;
    },
    apiErrors() {
      return this.$store.state.Api.errors;
    },
    amount: {
      get() {
        return this.$store.state.Payment.paymentData.amount;
      },
      set(amount) {
        this.$store.commit("Payment/UPDATE_AMOUNT", amount);
      },
    },
    account() {
      return this.$store.state.Auth.account;
    },
    ui() {
      const locale = this.$i18n && this.$i18n.locale === "en" ? "en" : "uk";

      return {
        uk: {
          amountLabel: "Введіть суму до оплати",
          accountLabel: "Рахунок",
          debtLabel: "Борг",
          overpayLabel: "Переплата",
          chooseCardTitle: "Виберіть картку для оплати",
          payAction: "Оплатити",
          orEnterNewCard: "або введіть нову картку",
          enterCardForPayment: "Введіть номер картки для оплати",
          cardNumberLabel: "Номер картки",
          expiryLabel: "Термін дії",
          helperMessage:
            "Щоб оплатити, виберіть збережену картку або введіть дані нової картки",
          securePayment: "Безпечний платіж",
          payButton: "Сплатити",
          enterAmountError: "Введіть суму оплати",
          fillCardError: "Заповніть дані картки",
          currency: "грн",
        },
        en: {
          amountLabel: "Enter payment amount",
          accountLabel: "Account",
          debtLabel: "Debt",
          overpayLabel: "Overpayment",
          chooseCardTitle: "Choose a card for payment",
          payAction: "Pay",
          orEnterNewCard: "or enter a new card",
          enterCardForPayment: "Enter card number for payment",
          cardNumberLabel: "Card number",
          expiryLabel: "Expiry date",
          helperMessage:
            "To pay, choose a saved card or enter new card details",
          securePayment: "Secure payment",
          payButton: "Pay",
          enterAmountError: "Enter payment amount",
          fillCardError: "Fill in the card details",
          currency: "UAH",
        },
      }[locale];
    },
    getPhone() {
      return (
        this.$store.state.Auth.phoneNumber ||
        (this.$store.state.Settings.phone &&
          this.$store.state.Settings.phone.phone_for_save_card)
      );
    },
    paymentPhone() {
      return this.getPhone || this.fallbackPhone;
    },
    hasSavedCardSelection() {
      return !!this.selectedSavedCardId;
    },
    hasCompleteNewCardData() {
      return (
        this.cardData.number.replace(/\s/g, "").length === 16 &&
        this.cardData.expiry.length === 5 &&
        this.cardData.cvv.length === 3
      );
    },
    normalizedEnteredCardNumber() {
      return this.cardData.number.replace(/\D/g, "");
    },
    enteredCardMatchesSavedCard() {
      const entered = this.normalizedEnteredCardNumber;

      if (entered.length !== 16) {
        return false;
      }

      return this.cards.some((card) => {
        const masked = String(card.masked || card.pan || "");
        const visibleDigits = masked.replace(/[^\d*]/g, "");
        const firstMatch = visibleDigits.match(/^(\d{4,6})/);
        const lastMatch = visibleDigits.match(/(\d{4})$/);
        const firstDigits = firstMatch ? firstMatch[1] : "";
        const lastDigits = lastMatch ? lastMatch[1] : "";

        return (
          firstDigits &&
          lastDigits &&
          entered.startsWith(firstDigits) &&
          entered.endsWith(lastDigits)
        );
      });
    },
    canPay() {
      return (
        Number(this.amount) >= MIN_PAYMENT_AMOUNT &&
        this.hasCompleteNewCardData &&
        !this.enteredCardMatchesSavedCard
      );
    },
    formattedAmount() {
      const amount = Number(this.amount) || 0;
      return amount.toFixed(2);
    },
    formattedDebt() {
      const balance = Number(this.account && this.account.balance) || 0;
      return Math.abs(balance).toFixed(2);
    },
    debtLabel() {
      const balance = Number(this.account && this.account.balance) || 0;
      return balance < 0 ? this.ui.overpayLabel : this.ui.debtLabel;
    },
    debtValueClass() {
      const balance = Number(this.account && this.account.balance) || 0;
      return balance < 0 ? "is-overpay" : "is-debt";
    },
    payButtonLabel() {
      return `${this.ui.payButton} ${this.formattedAmount} ${this.ui.currency}`;
    },
    animationActionLabels() {
      const locale = this.$i18n && this.$i18n.locale === "en"
        ? "en"
        : this.$i18n && this.$i18n.locale === "ru"
          ? "ru"
          : "uk";

      return {
        uk: {
          success: "До рахунку",
          error: "Спробувати ще раз",
        },
        ru: {
          success: "К счету",
          error: "Попробовать снова",
        },
        en: {
          success: "Back to account",
          error: "Try again",
        },
      }[locale];
    },
    cardsSignature() {
      return (this.cards || [])
        .map((card) => card.card_id || card.card_token || card.masked || "")
        .join("|");
    },
  },
  watch: {
    account: {
      handler(account) {
        const defaultAmount = this.getDefaultAmount(account);

        if (defaultAmount !== null) {
          this.amount = defaultAmount;
          this.amountInput = this.formatAmountValue(defaultAmount);
        }
      },
      immediate: true,
    },
    cardsSignature(newValue, oldValue) {
      if (!oldValue || !newValue || newValue === oldValue) {
        return;
      }

      this.resetNewCardEntry();
    },
    enteredCardMatchesSavedCard(isMatched) {
      if (isMatched) {
        this.resetNewCardEntry();
      }
    },
  },
  async created() {
    this.$store.commit("Payment/SET_PAYMENT_URL", null);
    this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
    this.$store.commit("Payment/SET_SAVE_CARD", false);
    this.$store.commit("Payment/SET_CARD_TOKEN", null);
    this.fallbackPhone = this.generateFallbackPhone();
    this.resetNewCardEntry();
    await this.$store.dispatch("Settings/getPhone");
    await this.$store.dispatch("Auth/setUserPhone");
    await this.preparePaymentPage();
    if (!this.amount) {
      const defaultAmount = this.getDefaultAmount(this.account);
      this.amount = defaultAmount !== null ? defaultAmount : this.amount;
    }
    this.amountInput = this.formatAmountValue(this.amount);
    this.runCheck();
  },
  async activated() {
    await this.refreshSavedCards();
  },
  destroyed() {
    clearInterval(this.watchInterval);
    sessionStorage.removeItem(PAYMENT_FLOW_SESSION_KEY);
  },
  methods: {
    wait(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    async refreshSavedCardsUntilVisible(savedCardId = null) {
      const normalizedId = Number(savedCardId || 0);

      for (let attempt = 0; attempt < 4; attempt += 1) {
        await this.$store.dispatch("Payment/getCardsList");

        if (!normalizedId) {
          return;
        }

        const hasCard = (this.$store.state.Payment.cards || []).some(
          (card) => Number(card.card_id || card.card_token) === normalizedId
        );

        if (hasCard) {
          return;
        }

        await this.wait(450);
      }
    },
    async syncSavedCardsAfterPayment() {
      const response = this.paymentResponse || {};
      const savedCardId = response.saved_card_id || response.card_id || null;

      await this.refreshSavedCardsUntilVisible(savedCardId);
    },
    async preparePaymentPage() {
      await this.$store.dispatch(
        "Auth/getUserAccount",
        this.$route.params.account
      );
      await this.refreshSavedCards();
      this.$store.commit("Payment/SET_USER_LS", this.$route.params.account);
    },
    async refreshSavedCards() {
      await this.$store.dispatch("Payment/getCardsList");
    },
    resetNewCardEntry() {
      this.selectedSavedCardId = null;
      this.cardData.number = "";
      this.cardData.expiry = "";
      this.cardData.cvv = "";
      this.validationError = null;
    },
    openPaymentAnimation() {
      sessionStorage.setItem(PAYMENT_FLOW_SESSION_KEY, "1");
      this.showPaymentAnimation = true;
      this.paymentAnimationResult = null;
    },
    finishPaymentAnimation(result) {
      this.showPaymentAnimation = true;
      this.paymentAnimationResult = result;
    },
    closePaymentAnimation() {
      sessionStorage.removeItem(PAYMENT_FLOW_SESSION_KEY);
      this.showPaymentAnimation = false;
      this.paymentAnimationResult = null;
    },
    isAmountBelowMinimum() {
      const amount = Number(this.amount);
      return Number.isFinite(amount) && amount > 0 && amount < MIN_PAYMENT_AMOUNT;
    },
    getMinAmountError() {
      return `Сума платежу менша за ${MIN_PAYMENT_AMOUNT} грн.`;
    },
    async handleAnimationAction() {
      if (this.paymentAnimationResult && this.paymentAnimationResult.success) {
        this.closePaymentAnimation();
        await this.$router.push({
          name: "Account",
          params: { account: this.$route.params.account },
        });
        return;
      }
      this.closePaymentAnimation();
    },
    async selectSavedCard(cardToken) {
      this.selectedSavedCardId = null;
      this.cardData.number = "";
      this.cardData.expiry = "";
      this.cardData.cvv = "";
      this.validationError = null;
      if (!this.amount || Number(this.amount) <= 0) {
        this.validationError = this.ui.enterAmountError;
        return;
      }
      if (this.isAmountBelowMinimum()) {
        this.validationError = this.getMinAmountError();
        return;
      }
      await this.runPaymentRequest(cardToken);
    },
    formatAmountValue(value) {
      const numeric = Number(value) || 0;
      return numeric ? numeric.toFixed(2) : "";
    },
    getDefaultAmount(account) {
      const balance = Number(account && account.balance);

      if (!Number.isFinite(balance)) {
        return null;
      }

      if (balance > 0) {
        return balance;
      }

      if (balance < 0) {
        return 100;
      }

      return null;
    },
    formatAmount() {
      const sanitized = String(this.amountInput)
        .replace(",", ".")
        .replace(/[^\d.]/g, "");
      const parts = sanitized.split(".");
      const normalized =
        parts.length > 1
          ? `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`
          : parts[0];
      this.amountInput = normalized;
      this.amount = normalized ? Number(normalized) : null;
      this.validationError = this.isAmountBelowMinimum()
        ? this.getMinAmountError()
        : null;
    },
    formatCardNumber() {
      this.selectedSavedCardId = null;
      const digits = this.cardData.number.replace(/\D/g, "").slice(0, 16);
      const groups = digits.match(/.{1,4}/g);
      this.cardData.number = groups ? groups.join(" ") : "";
      this.validationError = null;
    },
    formatExpiry() {
      this.selectedSavedCardId = null;
      const digits = this.cardData.expiry.replace(/\D/g, "").slice(0, 4);
      if (digits.length <= 2) {
        this.cardData.expiry = digits;
      } else {
        this.cardData.expiry = `${digits.slice(0, 2)}/${digits.slice(2)}`;
      }
      this.validationError = null;
    },
    formatCvv() {
      this.selectedSavedCardId = null;
      this.cardData.cvv = this.cardData.cvv.replace(/\D/g, "").slice(0, 3);
      this.validationError = null;
    },
    generateFallbackPhone() {
      const prefixes = ["+38093", "+38096", "+38097", "+38050"];
      const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
      const randomPart = Math.floor(Math.random() * 10000000)
        .toString()
        .padStart(7, "0");
      return `${prefix}${randomPart}`;
    },
    validateForm() {
      if (!this.amount || Number(this.amount) <= 0) {
        this.validationError = this.ui.enterAmountError;
        return false;
      }
      if (this.isAmountBelowMinimum()) {
        this.validationError = this.getMinAmountError();
        return false;
      }
      if (this.hasSavedCardSelection) {
        this.validationError = null;
        return true;
      }
      if (!this.hasCompleteNewCardData) {
        this.validationError = this.ui.fillCardError;
        return false;
      }
      this.validationError = null;
      return true;
    },
    async submitPayment() {
      if (!this.validateForm()) {
        return;
      }
      await this.runPaymentRequest();
    },
    buildClusterPayload(savedCardId = null) {
      if (savedCardId) {
        return {
          ls: this.$route.params.account,
          amount: Number(this.formattedAmount),
          phone: this.paymentPhone,
          card_id: savedCardId,
        };
      }
      return {
        ls: this.$route.params.account,
        amount: Number(this.formattedAmount),
        card_number: this.cardData.number.replace(/\s/g, ""),
        card_expiry: this.cardData.expiry,
        card_cvv: this.cardData.cvv,
        phone: this.paymentPhone,
      };
    },
    normalizeStripeResponse(raw) {
      if (!raw) return raw;
      if (raw.payment_state) return raw;
      if (raw.data && raw.data.payment_state) return raw.data;
      if (raw.data && raw.data.data && raw.data.data.payment_state) {
        return raw.data.data;
      }
      if (raw.response && raw.response.data) {
        return this.normalizeStripeResponse(raw.response.data);
      }
      return raw;
    },
    findStripe3dsPayload(raw, depth = 0) {
      if (!raw || depth > 4 || typeof raw !== "object") return null;

      const hasStripe3dsState = raw.payment_state === "stripe_3ds_required";
      const hasStripe3dsMessage =
        typeof raw.message === "string" &&
        raw.message.toLowerCase().includes("stripe 3ds");

      if (
        raw.client_secret &&
        (hasStripe3dsState || hasStripe3dsMessage || raw.intent_type)
      ) {
        return raw;
      }

      const nestedKeys = ["data", "response", "result", "payload", "error"];
      for (const key of nestedKeys) {
        const nested = this.findStripe3dsPayload(raw[key], depth + 1);
        if (nested) return nested;
      }

      return null;
    },
    isStripe3dsRequired(response) {
      return (
        response &&
        (response.payment_state === "stripe_3ds_required" ||
          this.findStripe3dsPayload(response))
      );
    },
    loadStripeScript() {
      if (window.Stripe) {
        return Promise.resolve();
      }

      const existingScript = document.getElementById(STRIPE_SCRIPT_ID);
      if (existingScript) {
        return new Promise((resolve, reject) => {
          existingScript.addEventListener("load", resolve, { once: true });
          existingScript.addEventListener("error", reject, { once: true });
        });
      }

      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.id = STRIPE_SCRIPT_ID;
        script.src = "https://js.stripe.com/v3/";
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async getStripe(publishableKey) {
      if (!publishableKey) {
        throw new Error("Stripe publishable_key is missing.");
      }

      await this.loadStripeScript();

      if (!this.stripe || this.stripePublishableKey !== publishableKey) {
        this.stripe = window.Stripe(publishableKey);
        this.stripePublishableKey = publishableKey;
      }

      if (!this.stripe) {
        throw new Error("Stripe не вдалося завантажити.");
      }

      return this.stripe;
    },
    async handleStripe3ds(data) {
      if (!data.client_secret) {
        throw new Error("Stripe 3DS client_secret is missing.");
      }

      const clientSecret = String(data.client_secret);
      const publishableKey = data.publishable_key || STRIPE_PUBLISHABLE_KEY;
      const stripe = await this.getStripe(publishableKey);
      const isSetupIntent =
        data.intent_type === "setup" || clientSecret.startsWith("seti_");
      const result = isSetupIntent
        ? await stripe.confirmCardSetup(clientSecret)
        : await stripe.confirmCardPayment(clientSecret);

      if (result.error) {
        console.log("3DS error", {
          code: result.error.code,
          message: result.error.message,
          clientSecretPrefix: clientSecret.slice(0, 12),
          keyPrefix: publishableKey ? publishableKey.slice(0, 16) : null,
          intentType: data.intent_type,
        });
        throw new Error(STRIPE_3DS_CANCELED_MESSAGE);
      }

      return result;
    },
    async runStripe3dsIfRequired(response) {
      const normalized = this.normalizeStripeResponse(response);
      const stripe3dsPayload = this.findStripe3dsPayload(response) || normalized;

      if (!this.isStripe3dsRequired(stripe3dsPayload)) {
        return false;
      }

      await this.handleStripe3ds(stripe3dsPayload);
      return true;
    },
    async dispatchClusterPayment(payload) {
      await this.$store.dispatch("Payment/getClusterPaymentPage", payload);
      return this.paymentResponse;
    },
    async runPaymentRequest(savedCardId = null) {
      const payload = this.buildClusterPayload(savedCardId);

      try {
        this.openPaymentAnimation();
        let result = await this.dispatchClusterPayment(payload);

        if (await this.runStripe3dsIfRequired(this.paymentResponse)) {
          this.$store.commit("Payment/SET_PAYMENT_URL", null);
          this.$store.commit("Payment/SET_PAYMENT_RESPONSE", null);
          result = await this.dispatchClusterPayment(payload);
        }

        if (
          result === false &&
          this.apiErrors &&
          this.apiErrors.message
        ) {
          await this.syncSavedCardsAfterPayment();
          this.finishPaymentAnimation({
            success: false,
            message: this.apiErrors.message,
          });
          return;
        }
        if (
          this.paymentResponse &&
          this.paymentResponse.success === false &&
          this.paymentResponse.message
        ) {
          await this.syncSavedCardsAfterPayment();
          this.finishPaymentAnimation({
            success: false,
            message: this.paymentResponse.message,
          });
          return;
        }
        if (
          this.paymentResponse &&
          this.paymentResponse.success === true &&
          (this.paymentResponse.status === 1 ||
            this.paymentResponse.payment_status === "accepted")
        ) {
          await this.syncSavedCardsAfterPayment();
          this.resetNewCardEntry();
          this.finishPaymentAnimation({
            success: true,
            message:
              this.paymentResponse.message || this.$t("pay.paymentSuccessful"),
          });
          return;
        }
        if (
          this.paymentResponse &&
          this.paymentResponse.success === true &&
          this.paymentResponse.redirect_url
        ) {
          this.closePaymentAnimation();
          this.$store.commit(
            "Payment/SET_PAYMENT_URL",
            this.paymentResponse.redirect_url
          );
          return;
        }
        if (
          this.paymentResponse &&
          Object.keys(this.paymentResponse).indexOf("mode") >= 0
        ) {
          if (this.paymentResponse.mode === 1) {
            await this.syncSavedCardsAfterPayment();
            this.resetNewCardEntry();
            this.finishPaymentAnimation({
              success: true,
              message:
                this.paymentResponse.message || this.$t("pay.paymentSuccessful"),
            });
          } else if (!this.paymentUrl) {
            await this.syncSavedCardsAfterPayment();
            this.finishPaymentAnimation({
              success: false,
              message:
                this.paymentResponse.message || this.$t("pay.paymentCanceled"),
            });
          }
          return;
        }
        await this.syncSavedCardsAfterPayment();
        this.finishPaymentAnimation({
          success: false,
          message:
            (this.paymentResponse && this.paymentResponse.message) ||
            this.$t("pay.paymentCanceled"),
        });
      } catch (e) {
        await this.syncSavedCardsAfterPayment();
        this.finishPaymentAnimation({
          success: false,
          message: e.message || this.$t("pay.paymentCanceled"),
        });
      }
    },
    runCheck() {
      this.watchInterval = setInterval(() => {
        if (this.$refs.payPage !== undefined) {
          try {
            const url = this.$refs.payPage.contentWindow.location.href;
            if (url.match(process.env.VUE_APP_PRODUCTION_URL) !== null) {
              clearInterval(this.watchInterval);
              this.$router.push(
                url.replace(process.env.VUE_APP_PRODUCTION_URL, "")
              );
            }
          } catch (e) {
            console.log("Waiting");
          }
        }
      }, 200);
    },
  },
};
</script>

<style scoped>
.payment-iframe-wrap {
  padding-top: 10px;
}

.iframe-container {
  overflow: auto;
  -webkit-overflow-scrolling: touch !important;
  height: calc(100vh - 120px);
  width: 100%;
}

.iframe-container iframe {
  height: calc(100vh - 120px);
  width: 1px;
  min-width: 100%;
  *width: 100%;
}

.payment-iframe {
  border: none !important;
}

.payment-screen {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 12px 12px 28px;
  box-sizing: border-box;
}

.summary-card {
  display: none;
}

.payment-form-card {
  width: 100%;
  margin-bottom: 16px;
  padding: 14px 16px 16px;
  background: #0e121b;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  box-sizing: border-box;
}

.payment-form-card {
  order: 1;
}

.summary-info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.summary-info-row + .summary-info-row {
  margin-top: 8px;
}

.summary-info-label {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
}

.summary-info-value {
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
}

.summary-debt-value {
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
}

.summary-debt-value.is-debt {
  color: #ff7d7d;
}

.summary-debt-value.is-overpay {
  color: #67d58c;
}

.amount-value-input {
  display: block;
  width: 100%;
  height: 52px;
  padding: 0 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.015);
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  text-align: left;
  outline: none;
  box-sizing: border-box;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.02);
}

.amount-value-input::placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.amount-value-input:focus,
.payment-input:focus {
  border-color: rgba(90, 124, 255, 0.75);
}

.saved-cards-section {
  order: 2;
  width: 100%;
  margin-bottom: 16px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  box-sizing: border-box;
}

.section-title {
  margin-bottom: 12px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  text-align: center;
}

.saved-cards-list {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: 100%;
}

.saved-card-item {
  appearance: none;
  -webkit-tap-highlight-color: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  max-width: none !important;
  min-width: 100%;
  align-self: stretch;
  padding: 15px 16px;
  background: #0e121b;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  cursor: pointer;
  box-sizing: border-box;
  text-align: left;
  box-shadow: none;
  margin: 0;
  outline: none;
}

.saved-card-item.selected {
  border-color: rgba(255, 255, 255, 0.12);
  background: #0e121b;
  box-shadow: none;
}

.saved-card-item:focus,
.saved-card-item:focus-visible,
.saved-card-item:active {
  outline: none;
  border-color: rgba(255, 255, 255, 0.12);
  background: #0e121b;
  box-shadow: none;
}

.saved-card-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.card-type-image {
  width: 36px;
  height: auto;
  flex-shrink: 0;
  border-radius: 6px;
}

.card-number {
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.02em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-action {
  color: rgba(120, 150, 255, 0.9);
  font-size: 15px;
  font-weight: 700;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.card-action-arrow {
  font-size: 16px;
  line-height: 1;
}

.divider-text {
  display: none;
  order: 8;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  margin: 0 0 12px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 15px;
  text-align: center;
}

.divider-text::before,
.divider-text::after {
  content: "";
  flex: 1 1 auto;
  height: 1px;
  min-width: 44px;
  background: rgba(255, 255, 255, 0.1);
}

.form-group {
  order: 3;
  width: 100%;
  margin-bottom: 14px;
  box-sizing: border-box;
}

.card-meta-row {
  order: 4;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  margin-bottom: 14px;
}

.form-group-half {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 9px;
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
  font-weight: 600;
  text-align: left;
}

.payment-input {
  display: block;
  width: 100%;
  height: 52px;
  padding: 0 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.015);
  color: #ffffff;
  font-size: 15px;
  outline: none;
  box-sizing: border-box;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.02);
}

.payment-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.error-message {
  order: 5;
  margin: 0 0 16px;
  padding: 10px 12px;
  color: #f87c7c;
  background: rgba(248, 124, 124, 0.18);
  border-left: 4px solid #f87c7c;
  border-radius: 6px;
  text-align: center;
  font-weight: 700;
}

.helper-message {
  order: 7;
  margin: 12px 0 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 14px;
  text-align: center;
  line-height: 1.4;
}

.amount-entry {
  order: 6;
  width: 100%;
  margin-bottom: 0;
}

.amount-entry-label {
  display: block;
  margin: 0 0 9px;
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
  font-weight: 600;
  text-align: left;
}

.pay-button-wrap {
  order: 9;
  width: 100%;
  margin-top: 4px;
  box-sizing: border-box;
  max-width: none;
}

.section-title-left {
  order: 2;
  text-align: center;
  margin-top: 0;
  margin-bottom: 16px;
}

.payment-form-summary-row {
  margin-bottom: 0;
}

.payment-form-divider {
  height: 2px;
  margin: 14px 0 16px;
  background: rgba(255, 255, 255, 0.16);
}

.submit-button {
  display: block;
  width: 100%;
  max-width: none !important;
  height: 56px;
  border: none;
  border-radius: 12px;
  background: #4f78f6;
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  box-sizing: border-box;
  box-shadow: 0 10px 24px rgba(79, 120, 246, 0.22);
  transition: background-color 0.2s ease, opacity 0.2s ease, transform 0.2s ease;
  margin: 0;
}

.submit-button:hover:not(:disabled) {
  background: #5a84ff;
}

.submit-button:active:not(:disabled) {
  transform: translateY(1px);
}

.submit-button:disabled {
  background: rgba(79, 120, 246, 0.38);
  color: rgba(255, 255, 255, 0.62);
  cursor: not-allowed;
  box-shadow: none;
}

.security-badge {
  order: 10;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 14px 0 18px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
}

.security-lock {
  font-size: 12px;
}

.security-card {
  height: 16px;
  width: auto;
}

.debt-note {
  order: 11;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 10px;
  padding: 14px 16px;
  background: #0e121b;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  box-sizing: border-box;
}

.debt-note-text {
  display: block;
  flex: 1;
  width: 100%;
  color: rgba(255, 255, 255, 0.96);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  text-align: center;
}

@media (max-width: 420px) {
  .payment-screen {
    padding-left: 12px;
    padding-right: 12px;
  }

  .summary-card {
    padding: 14px;
  }

  .amount-value-input {
    font-size: 15px;
  }

  .debt-note {
    padding: 14px 16px;
  }

  .debt-note-text {
    font-size: 16px;
  }
}

@media (max-width: 375px) {
  .payment-screen {
    padding-left: 10px;
    padding-right: 10px;
    padding-bottom: 24px;
  }

  .summary-card {
    padding: 12px;
    border-radius: 14px;
  }

  .saved-card-item {
    padding: 12px 14px;
    border-radius: 12px;
  }

  .payment-input,
  .amount-value-input {
    height: 50px;
  }

  .amount-value-input {
    font-size: 15px;
  }

  .submit-button {
    height: 52px;
    font-size: 16px;
    border-radius: 12px;
  }

  .card-action {
    font-size: 14px;
  }

  .debt-note-text {
    font-size: 14px;
  }
}

@media (max-width: 360px) {
  .payment-screen {
    padding-left: 8px;
    padding-right: 8px;
  }

  .card-meta-row {
    gap: 10px;
  }

  .payment-input {
    padding-left: 12px;
    padding-right: 12px;
  }

  .amount-entry-label,
  .form-group label {
    font-size: 13px;
  }

  .card-number {
    font-size: 14px;
  }

  .helper-message,
  .divider-text {
    font-size: 13px;
  }
}
</style>
